// src/pages/NetworkAlerts/styles.js — estilos compartilhados do "Problema Geral"
// Botões, campos, abas, tabelas e diálogos vêm do tema; aqui fica só o que é layout da tela.
import { makeStyles } from "@material-ui/core/styles";
import { getTokens, brandAlpha } from "../../styles/tokens";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    /* ---------- página ---------- */
    tabs: {
      flex: "none",
      marginBottom: 16,
      borderBottom: `1px solid ${t.line}`,
    },
    tabPanel: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
    },

    /* ---------- botão com progresso ---------- */
    btnWrapper: { position: "relative" },
    buttonProgress: {
      color: t.brand,
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -10,
      marginLeft: -10,
    },

    /* ---------- barra de filtros ---------- */
    filterBar: {
      flex: "none",
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 12,
      marginBottom: 16,
    },
    filterItem: {
      width: 220,
      [theme.breakpoints.down("xs")]: { width: "100%" },
    },
    filterItemWide: {
      flex: "1 1 260px",
      minWidth: 220,
      maxWidth: 460,
      [theme.breakpoints.down("xs")]: { minWidth: 0, maxWidth: "none", width: "100%", flex: "none" },
    },
    filterPrefix: { color: t.textMuted },
    // filtro escolhido (Ativos / Encerrados / Todos): tinta da marca; o `&&` vence o hover padrão do Chip
    filterChipActive: {
      color: t.brand,
      backgroundColor: t.tint.soft,
      borderColor: brandAlpha(t.brand, 0.35),
      "&&:hover, &&:focus": { backgroundColor: t.tint.medium },
    },
    placeholder: { color: t.textMuted },

    /* ---------- índice local do ERP ---------- */
    indexLine: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 10,
      marginTop: -6,
      marginBottom: 14,
      fontSize: 12,
      color: t.textSecondary,
      "& > svg": { fontSize: 16, color: t.textMuted, flex: "none" },
    },
    indexText: { minWidth: 0 },
    indexError: { color: t.semantic.error },
    indexProgress: {
      flex: "1 1 120px",
      maxWidth: 220,
      height: 3,
      borderRadius: 2,
      backgroundColor: t.tint.soft,
      "& .MuiLinearProgress-bar": { backgroundColor: t.brand },
    },

    /* ---------- seleção de grupo (ERP indexado) ---------- */
    groupPaper: {
      boxShadow: t.shadow.lift,
    },
    groupOption: {
      fontSize: 14,
      minHeight: 36,
      padding: "6px 14px",
      '&[data-focus="true"]': { backgroundColor: t.muted },
      '&[aria-selected="true"]': {
        backgroundColor: t.tint.soft,
        color: t.brand,
      },
    },
    groupHeader: {
      position: "sticky",
      top: -8,
      zIndex: 1,
      padding: "6px 14px",
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.025em",
      lineHeight: "20px",
      color: t.textSecondary,
      backgroundColor: t.muted,
    },
    groupList: { padding: 0, listStyle: "none" },
    groupOptionRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      width: "100%",
      minWidth: 0,
    },
    groupOptionLabel: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    groupOptionParent: { color: t.textMuted, fontSize: 13 },
    groupOptionCount: {
      flex: "none",
      fontSize: 12,
      fontVariantNumeric: "tabular-nums",
      color: t.textSecondary,
    },
    groupSpinner: { color: t.brand, marginRight: 4 },

    /* ---------- avisos ---------- */
    warning: {
      flex: "none",
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      padding: "10px 14px",
      marginBottom: 16,
      borderRadius: t.radius.sm,
      fontSize: 13,
      lineHeight: 1.5,
      color: t.status.warn.fg,
      backgroundColor: t.status.warn.bg,
      "& svg": { fontSize: 18, flex: "none", marginTop: 1 },
    },

    /* ---------- cartão com tabela ---------- */
    // O cartão acompanha a altura do conteúdo; se passar da tela, quem rola é o miolo.
    card: {
      flex: "0 1 auto",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxShadow: t.shadow.card,
    },
    cardHead: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "8px 16px",
      padding: "12px 16px",
      borderBottom: `1px solid ${t.line}`,
    },
    cardHeadTitle: {
      display: "flex",
      alignItems: "baseline",
      gap: 6,
      minWidth: 0,
      fontSize: 14,
      color: t.textSecondary,
      "& b": {
        fontSize: 18,
        fontWeight: 600,
        color: t.text,
        fontVariantNumeric: "tabular-nums",
      },
    },
    cardHeadActions: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
      marginLeft: "auto",
    },
    tableWrap: {
      flex: "1 1 auto",
      minHeight: 0,
      overflow: "auto",
      ...theme.scrollbarStyles,
    },
    searchTable: { minWidth: 760 },
    alertsTable: { minWidth: 920 },
    cellPrimary: { fontWeight: 600, color: t.text },
    cellSecondary: { color: t.textSecondary },
    nowrap: { whiteSpace: "nowrap" },
    pagination: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      flexWrap: "wrap",
      gap: 8,
      padding: "8px 16px",
      borderTop: `1px solid ${t.line}`,
      fontSize: 13,
      color: t.textSecondary,
    },

    /* ---------- lista de alertas ---------- */
    alertCell: { maxWidth: 340, minWidth: 200 },
    alertTitle: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      minWidth: 0,
      fontWeight: 600,
      color: t.text,
    },
    alertTitleText: {
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    alertMessage: {
      marginTop: 2,
      fontSize: 12.5,
      lineHeight: 1.45,
      color: t.textSecondary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    secondaryLine: {
      display: "block",
      marginTop: 2,
      fontSize: 12,
      color: t.textMuted,
    },
    campaignLink: {
      display: "block",
      marginTop: 4,
      fontSize: 12,
      fontWeight: 500,
      color: t.brand,
      textDecoration: "none",
      "&:hover": { textDecoration: "underline" },
    },
    menuIcon: { minWidth: 32, color: t.textSecondary },
    menuDanger: {
      color: t.semantic.error,
      "& .MuiListItemIcon-root": { color: t.semantic.error },
    },

    /* ---------- estados vazios ---------- */
    steps: {
      display: "grid",
      gap: 8,
      margin: "0 auto 16px",
      maxWidth: 420,
      textAlign: "left",
      padding: 0,
      listStyle: "none",
    },
    step: {
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      fontSize: 13,
      lineHeight: 1.5,
      color: t.textSecondary,
    },
    stepNumber: {
      flex: "none",
      width: 22,
      height: 22,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 12,
      fontWeight: 600,
      color: t.brand,
      backgroundColor: t.tint.soft,
    },

    /* ---------- modais ---------- */
    // o <form> fica entre o papel do diálogo e o conteúdo: precisa repassar o flex para o miolo rolar
    dialogForm: {
      display: "flex",
      flexDirection: "column",
      flex: "1 1 auto",
      minHeight: 0,
      overflow: "hidden",
    },
    dialogSubtitle: {
      fontSize: 13,
      fontWeight: 400,
      color: t.textSecondary,
      marginTop: 2,
      letterSpacing: 0,
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 16,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    formColumn: {
      display: "flex",
      flexDirection: "column",
      gap: 16,
    },
    full: { gridColumn: "1 / -1" },
    field: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
    label: { fontSize: 12.5, fontWeight: 600, color: t.textSecondary },
    required: { color: t.semantic.error, marginLeft: 2 },
    help: { fontSize: 12, lineHeight: 1.45, color: t.textMuted },
    sectionTitle: {
      gridColumn: "1 / -1",
      fontSize: 13,
      fontWeight: 600,
      color: t.text,
      margin: "8px 0 0",
      paddingTop: 12,
      borderTop: `1px solid ${t.line}`,
    },
    sectionTitleFirst: { margin: 0, paddingTop: 0, borderTop: "none" },
    previewBox: {
      padding: "10px 14px",
      borderRadius: t.radius.sm,
      backgroundColor: t.muted,
      border: `1px solid ${t.line}`,
      fontSize: 14,
      lineHeight: 1.5,
      color: t.text,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
    previewEmpty: { color: t.textMuted },
    photoRow: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 10,
    },
    photoThumb: {
      width: 64,
      height: 64,
      flex: "none",
      borderRadius: t.radius.sm,
      border: `1px solid ${t.line}`,
      objectFit: "cover",
      backgroundColor: t.muted,
    },
    dangerText: { color: t.semantic.error },
    connectionOption: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      minWidth: 0,
      "& svg": { fontSize: 18, color: t.semantic.success },
    },
    summary: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "10px 14px",
      borderRadius: t.radius.sm,
      backgroundColor: t.muted,
      border: `1px solid ${t.line}`,
      fontSize: 13,
      color: t.textSecondary,
    },
    summaryValue: {
      fontSize: 18,
      fontWeight: 600,
      color: t.text,
      fontVariantNumeric: "tabular-nums",
    },
    acknowledge: {
      marginRight: 0,
      "& .MuiFormControlLabel-label": { fontSize: 13.5 },
    },
    result: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: 8,
      padding: "16px 0 8px",
      "& svg": { fontSize: 40, color: t.semantic.success },
    },
    resultTitle: { fontSize: 16, fontWeight: 600, color: t.text },
    resultText: { fontSize: 13, color: t.textSecondary, maxWidth: 360 },
  };
});

export default useStyles;
